/**
 * 用語詳細。作り直しの用語（`getTermDetail`）1 件を 1 画面に集約する。
 *
 * 見出しの下にタブを 2 つ置く — 「基本情報」と「使われている場所 N」（Dhik 2026-09-29「用語も用語の
 * 基本情報と使われている場所の 2 タブにして、基本情報の方は表形式にしてくれ」）。基本情報は
 * 欄 ／ 値 の表 1 枚で、名前・定義・種別・値のタイプ・属するクラス・単位・取りうる値・状態遷移・
 * 揺れ表記を並べる（欄の定義は `term-info-table.tsx`）。「使われている場所 N」には**誰がこの語を
 * 指しているか**を置く。
 *
 * **未設定の欄を隠さない。** 種別・値のタイプ・属するクラス・単位は復元が未設定のまま戻す欄で
 * （244 件のうち種別未設定が 79 件）、埋めるのは人である。空欄にすると「この語には無い欄」と
 * 「まだ誰も書いていない欄」が同じ見た目になるので、「未設定」と書いて出す。
 *
 * 種別と値のタイプで書けない欄は出さない（`term-field-applicability.ts`）。種別か値のタイプが
 * 未設定の古い用語は判定できないので、欄を全部出し、無いものは「未設定」と言う。種別は札
 * （クラス ＝ 塗り、プロパティ ＝ 枠、値 ＝ 破線の枠）で、状態遷移は表で出す。取りうる値の
 * 並びの順に意味があることは、欄の見出しの ⓘ の title で断る。型の名前と未翻訳の印は名前の title に回す。
 *
 * `?pj=` を渡すと、同じ用語を変更前（`side: 'before'`）と変更後（`side: 'after'`）の 2 回引き、
 * 業務詳細と同じくタブの並びの右端に「変更前と比較」のボタンを置く。開けば 欄 ／ 変更前 ／ 変更後 の
 * 3 列、閉じれば変更後の 1 面の表で変わった欄のセルに地色を敷く。**案件で 1 欄も変わっていなければ
 * ボタンは押せず 1 面のまま**にする（`isPjUnchanged`）。
 * **この画面から出るリンクは全部その案件を持ったまま繋ぐ** — 戻るリンクも、取りうる値の用語も、定義文の用語リンクもである。1 本でも
 * 落とすと、案件を選んで開いた利用者がそこだけ master へ落ち、案件でしか存在しない用語なら
 * 「見つかりません」に着く。落ちたことは画面に出ない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの用語 1 件を、欄 ／ 値 の表の「基本情報」と参照元の「使われている場所」の 2 タブに分けて描き、案件モードでは変更前との比較を切り替えられるようにする。
 * @graph-connects useTermDetail [calls] 用語詳細の取得
 * @graph-connects pjLinkSearch [calls] 案件を次の画面へ引き継ぐ検索条件
 * @graph-connects TermInfoTable [calls] 基本情報の 1 面の表
 * @graph-connects PjCompareTable [calls] 案件モードの 3 列の表
 * @graph-connects DiffCompareToggle [calls] 変更前と比較のボタン
 * @graph-connects DetailTabs [calls] 見出しの下のタブ
 */
import type { TermReferrer, TermDetailOutput } from '@cortex/domain-spec-console';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DetailTabs, type DetailTab } from '../components/spec/DetailTabs.js';
import { DiffCompareToggle, type DiffSide } from '../components/spec/DiffSplitPanes.js';
import { ErrorState, NotFoundState } from '../components/spec/fallback-states.js';
import { NodeTypeBadge } from '../components/spec/NodeTypeBadge.js';
import { displayLabel } from '../components/spec/UntranslatedBadge.js';
import { toElementId, useTermDetail } from '../hooks/queries/spec-graph-element-term-queries.js';
import { isNotFoundError } from '../lib/orpc-errors.js';
import { SPEC_NODE_TYPE_VALUES, type SpecNodeTypeValue } from '../lib/spec-node-types.js';
import { useCompareSearch } from './flow-workspace/use-compare-search.js';
import { TERMS_ROUTE, TERM_DETAIL_ROUTE } from './route-paths.js';
import { pjLinkSearch } from './pj-link.js';
import { pjTextOf } from './pj-search.js';
import { applicableTermFields, termFieldSpecs, TermInfoTable } from './term-info-table.js';
import { TermNoteEditor } from './term-note-editor.js';
import {
  buildPjCompareRows,
  isPjUnchanged,
  pjCompareRowState,
  PjCompareTable,
  type PjSides,
} from './pj-compare-table.js';
import { usePjNodePresence } from './pj-master-presence.js';

/** ローディング時に出すスケルトンの区画数。詳細の区画数に揃える。 @graph-connects none */
const SKELETON_SECTIONS = 4;

/**
 * 契約が返した型名を、バッジが描ける型かどうかで判定する。
 *
 * 契約は型名を自由文字列で運ぶ（作り直しの 11 型と今の 13 型が同じ表に載らないため）。
 * 判定せずにバッジへ渡すと、知らない型名が来たときに色の表を引けず描画ごと落ちる。
 *
 * @param nodeType 契約が返した型名
 * @returns バッジが描ける型なら true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 契約が返した型名がバッジの描ける仕様グラフのノード型かを判定する。
 * @graph-connects none
 */
export function isSpecNodeTypeValue(nodeType: string): nodeType is SpecNodeTypeValue {
  return SPEC_NODE_TYPE_VALUES.some((value) => value === nodeType);
}

/**
 * 一覧へ戻るリンク。案件を持ったまま帰す。
 *
 * 落とすと、案件を選んで開いた利用者が案件の外れた master の一覧へ帰り、外れたことは画面に
 * 出ない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語詳細から用語集へ、案件を引き継いで戻るリンクを描く。
 * @graph-connects pjLinkSearch [calls] 戻るリンクへ案件を引き継ぐ検索条件
 */
function TermBackLink({ pj }: { pj: string | undefined }) {
  const { t } = useTranslation();
  return (
    <Link
      to={TERMS_ROUTE}
      {...pjLinkSearch(pj)}
      className="text-sm text-[var(--color-accent-primary)] underline"
    >
      {t('spec.backToList')}
    </Link>
  );
}

/** 取得中のスケルトン。 @graph-connects none */
function TermDetailSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('common.loading')}>
      {Array.from({ length: SKELETON_SECTIONS }, (_, index) => (
        <Skeleton key={index} variant="rectangular" height="7rem" />
      ))}
    </div>
  );
}

/**
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 作り直しの用語詳細を、読めない ID は未存在へ、案件モードなら変更前と変更後の取得へ、それ以外はマスターの取得へ振り分ける。
 * @graph-connects TermDetailSingle [calls] マスターの詳細
 * @graph-connects TermDetailCompare [calls] 案件モードの変更前と変更後の取得
 * @graph-connects pjTextOf [calls] 検索条件の案件番号を文字列へ戻す
 */
export function TermDetailPage() {
  const { termId } = useParams({ from: TERM_DETAIL_ROUTE });
  const pj = pjTextOf(useSearch({ from: TERM_DETAIL_ROUTE }).pj);
  const termNumber = toElementId(termId);
  const pjNumber = toElementId(pj);

  if (termNumber === undefined) {
    return <NotFoundState id={termId} action={<TermBackLink pj={pj} />} />;
  }
  if (pjNumber === undefined) {
    return <TermDetailSingle termId={termId} termNumber={termNumber} />;
  }
  return <TermDetailCompare termId={termId} termNumber={termNumber} pj={pj} pjNumber={pjNumber} />;
}

/**
 * 案件を選んでいないときの詳細を、取得中・未存在・失敗・本体の 4 状態で描く。本体には左右に同じ
 * 姿を渡す（差分が無いので比較のボタンも地色も出ない）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business master の用語詳細を取得し、取得中・未存在・失敗・本体の 4 状態で描く。
 * @graph-connects useTermDetail [calls] 用語詳細の取得
 * @graph-connects TermDetailBody [calls] 詳細の本体
 */
function TermDetailSingle({ termId, termNumber }: { termId: string; termNumber: number }) {
  const { data, isLoading, error } = useTermDetail(termNumber);

  if (isNotFoundError(error)) {
    return <NotFoundState id={termId} action={<TermBackLink pj={undefined} />} />;
  }
  if (error !== null && error !== undefined) {
    return <ErrorState error={error} />;
  }
  if (isLoading || data === undefined) {
    return <TermDetailSkeleton />;
  }

  return <TermDetailBody sides={{ before: data, after: data }} pj={undefined} />;
}

/** `TermDetailCompare` の props。 @graph-connects none */
interface TermDetailCompareProps {
  termId: string;
  termNumber: number;
  pj: string | undefined;
  pjNumber: number;
}

/**
 * 案件モードの用語詳細。変更前と変更後を引き、揃ってから本体へ渡す（1 面の表と 3 列の表の
 * 切り替えは本体が持つ）。
 *
 * 案件が足した用語は変更前を、消した用語は変更後を引かない。引くと必ず未存在で返る。
 * 片方だけで描くと「差分なし」が一瞬出てから入れ替わるので、両方の答えが揃うまで待つ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件モードで用語の変更前と変更後を引き、揃ってから詳細の本体へ渡す。
 * @graph-connects usePjNodePresence [calls] 案件が足した・消した用語かの判定
 * @graph-connects useTermDetail [calls] 変更前・変更後の取得
 * @graph-connects TermDetailBody [calls] 詳細の本体
 */
function TermDetailCompare({ termId, termNumber, pj, pjNumber }: TermDetailCompareProps) {
  const presence = usePjNodePresence(pjNumber, termNumber);
  const before = useTermDetail(termNumber, pjNumber, 'before', presence.master === 'present');
  const after = useTermDetail(termNumber, pjNumber, 'after', presence.applied === 'present');

  const beforeAbsent = presence.master === 'absent' || isNotFoundError(before.error);
  const afterAbsent = presence.applied === 'absent' || isNotFoundError(after.error);
  if (beforeAbsent && afterAbsent) {
    return <NotFoundState id={termId} action={<TermBackLink pj={pj} />} />;
  }
  const failure = (beforeAbsent ? null : before.error) ?? (afterAbsent ? null : after.error);
  if (failure !== null && failure !== undefined) {
    return <ErrorState error={failure} />;
  }
  if ((!beforeAbsent && before.data === undefined) || (!afterAbsent && after.data === undefined)) {
    return <TermDetailSkeleton />;
  }

  return (
    <TermDetailBody
      sides={{
        before: beforeAbsent ? null : (before.data ?? null),
        after: afterAbsent ? null : (after.data ?? null),
      }}
      pj={pj}
    />
  );
}

/** `TermDetailBody` の props。 @graph-connects none */
interface TermDetailBodyProps {
  /** 左右の姿。案件を選んでいなければ両側に同じ姿を渡す。片方は必ず在る。 */
  sides: PjSides<TermDetailOutput>;
  /** いま重ねている案件番号。マスターを見ていれば undefined。 */
  pj: string | undefined;
}

/** 用語詳細のタブ。 @graph-connects none */
type TermTab = 'info' | 'usage';

/**
 * 用語詳細の本体。見出し（型の札 ・ 名前）と、基本情報 ／ 使われている場所 N の 2 つのタブを描く。
 * 既定は「基本情報」のタブ。型の札は、どのアウトプットの詳細かを見失わないために名前の左に置く
 * （Dhik 2026-09-29「流石にないと迷子になる」）。
 *
 * **案件モードでは、タブの並びの右端に「変更前と比較」のボタンを置く**（業務詳細と同じ置き場所・
 * 同じ部品。Dhik 2026-09-29「用語に変更前と比較のボタンがないのでつけてくれ。一貫性持たしてくれ」）。
 * 開けば基本情報を 欄 ／ 変更前 ／ 変更後 の 3 列の表にし、閉じれば変更後の 1 面の表で、変わった欄の
 * 値のセルに地色を敷く。開閉は URL に持つ（`useCompareSearch`）。案件で 1 欄も変わっていなければ
 * ボタンは押せず、1 面の表のままにする（`isPjUnchanged`）。
 *
 * **案件モードでは基本情報の下に備考の入力欄を置く**（`TermNoteEditor`）。備考は人が画面からだけ
 * 書ける欄で、書くと案件に承認待ちの変更として積まれる。案件で消した用語には出さない。
 *
 * 見出しと参照元は変更後の姿で描く（案件で消した用語だけ変更前）。足した・消した用語でも見出しに
 * 札を付けない（3 列の表の空札と 1 面の表の地色で読める）。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語詳細を見出しと「基本情報」「使われている場所」の 2 タブで描き、案件モードでは「変更前と比較」で基本情報を 1 面の表と欄ごとの左右比較の間で切り替える。
 * @graph-connects termFieldSpecs [calls] 基本情報の欄の定義
 * @graph-connects applicableTermFields [calls] 書ける欄だけを残す
 * @graph-connects buildPjCompareRows [calls] 欄ごとの左右の行
 * @graph-connects isPjUnchanged [calls] 差分が 1 つも無いかの判定
 * @graph-connects pjCompareRowState [calls] 行の状態の判定
 * @graph-connects useCompareSearch [calls] 左右比較の開閉
 * @graph-connects DiffCompareToggle [calls] 変更前と比較のボタン
 * @graph-connects PjCompareTable [calls] 3 列の表
 * @graph-connects TermInfoTable [calls] 1 面の表
 * @graph-connects NodeTypeBadge [calls] 名前の左の型の札
 * @graph-connects DetailTabs [calls] タブの並び
 * @graph-connects displayLabel [calls] 表示名として実際に描く文字列を決める
 * @graph-connects TermReferenceList [calls] 参照元の並び
 * @graph-connects TermNoteEditor [calls] 案件モードの備考の入力欄
 */
function TermDetailBody({ sides, pj }: TermDetailBodyProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TermTab>('info');
  const shown = sides.after ?? sides.before;
  const rows = buildPjCompareRows<TermDetailOutput>(
    sides,
    applicableTermFields(termFieldSpecs(t, pj), sides),
  );
  const pjMode = pj !== undefined;
  const hasDiff = pjMode && !isPjUnchanged(sides, rows);
  const compare = useCompareSearch(hasDiff);
  /* 左右の片方は必ず在る（両方無ければ呼び出し側が未存在へ倒す）。 */
  if (shown === null) return null;

  const side: DiffSide = sides.after === null ? 'before' : 'after';
  const notes = [
    t('spec.nodeTypeTerm'),
    shown.label !== '' && shown.untranslated ? t('spec.untranslated') : undefined,
  ].filter((note) => note !== undefined);
  const tabs: DetailTab<TermTab>[] = [
    { id: 'info', label: t('termDetail.infoTab'), changed: hasDiff },
    { id: 'usage', label: t('termDetail.usageTab'), count: shown.referencedBy.length },
  ];
  const info = compare.split ? (
    <PjCompareTable firstColumn={t('pjCompare.fieldColumn')} rows={rows} />
  ) : (
    <TermInfoTable
      side={side}
      rows={rows.map((row) => ({
        key: row.key,
        label: row.label,
        value: side === 'after' ? row.after : row.before,
        changed: hasDiff && pjCompareRowState(row) !== 'unchanged',
      }))}
    />
  );

  return (
    <div className="flex min-h-0 flex-col gap-4 overflow-y-auto">
      <header className="flex flex-wrap items-center gap-2" data-id={shown.termId}>
        <NodeTypeBadge nodeType="Term" />
        <h1
          className="text-2xl font-bold text-[var(--color-text-primary)]"
          title={notes.join('\n')}
        >
          {displayLabel(shown.label, t, shown.untranslated)}
        </h1>
      </header>
      <DetailTabs
        tabs={tabs}
        value={tab}
        onChange={setTab}
        trailing={
          pjMode ? (
            <DiffCompareToggle
              split={compare.split}
              disabled={compare.disabled}
              onToggle={compare.toggle}
            />
          ) : undefined
        }
      />
      <div role="tabpanel" data-tab-panel={tab} className="flex flex-col gap-4">
        {tab === 'info' ? info : <TermReferenceList references={shown.referencedBy} />}
        {tab === 'info' && pjMode && sides.after !== null && (
          <TermNoteEditor
            key={`${String(sides.after.termId)}:${sides.after.note ?? ''}`}
            termId={sides.after.termId}
            pjNumber={Number(pj)}
            note={sides.after.note}
          />
        )}
      </div>
    </div>
  );
}

/** `TermReferenceList` の props。 @graph-connects none */
interface TermReferenceListProps {
  references: readonly TermReferrer[];
}

/**
 * この用語を指している要素の並び（「使われている場所」のタブの中身）。0 件ならタブの件数 0 で
 * 分かるので何も描かない。
 *
 * **どの欄で指しているかを落とさない。** 同じ要素が違う意味で 2 度指すことがある（業務の
 * 入力にも出力の区分にも同じクラスが載る）ので、要素の名前だけでは参照の意味が読めない。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 用語を指している要素を、型・名前・番号・指している欄つきで並べる。
 * @graph-connects isSpecNodeTypeValue [calls] 型名がバッジの描ける型かの判定
 */
function TermReferenceList({ references }: TermReferenceListProps) {
  const { t } = useTranslation();

  return (
    <>
      {references.length > 0 && (
        <ul className="flex flex-col gap-2 text-sm">
          {references.map((reference) => (
            <li
              key={`${reference.nodeType}:${String(reference.nodeId)}:${reference.field}`}
              className="flex flex-wrap items-center gap-2"
            >
              {isSpecNodeTypeValue(reference.nodeType) ? (
                <NodeTypeBadge nodeType={reference.nodeType} />
              ) : (
                <Badge variant="default" size="sm">
                  {reference.nodeType}
                </Badge>
              )}
              <span className="text-[var(--color-text-primary)]" data-id={reference.nodeId}>
                {displayLabel(reference.label, t)}
              </span>
              <span className="text-xs text-[var(--color-text-secondary)]">{reference.field}</span>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
